import { Admin, CustomRoutes, Resource } from "react-admin";
import { BrowserRouter, Route } from "react-router-dom";
import { ForgotPasswordPage, LoginPage, SetPasswordPage } from "ra-supabase";
import { authProvider, dataProvider, type Permissoes } from "./lib/provedores";
import { i18nProvider } from "./lib/i18n";
import { tema } from "./tema";
import { Layout } from "./layout/Layout";
import { Painel } from "./paginas/Painel";
import {
  ParametroFiscalCriar,
  ParametroFiscalLista,
  ParametroPrecoCriar,
  ParametroPrecoLista,
  ParametroSistemaCriar,
  ParametroSistemaLista,
  RegraPrecoCriar,
  RegraPrecoLista,
} from "./recursos/parametros";
import { AliquotaIcmsLista } from "./recursos/icms";
import { AuditoriaLista } from "./recursos/auditoria";
import { UsuarioConvidar, UsuarioEditar, UsuarioLista } from "./recursos/usuarios";
import { DadosEmpresa } from "./recursos/empresa";
import {
  ContaPortalCriar,
  ContaPortalEditar,
  ContaPortalLista,
  PortalCriar,
  PortalEditar,
  PortalLista,
} from "./recursos/portais";
import {
  FornecedorCriar,
  FornecedorEditar,
  FornecedorLista,
  GrupoCriar,
  GrupoEditar,
  SetorCriar,
  SetorEditar,
  FabricanteCriar,
  FabricanteEditar,
  UnidadeCriar,
  UnidadeEditar,
} from "./recursos/fornecedores";
import { ProdutoCriar, ProdutoEditar, ProdutoLista } from "./recursos/produtos";
import { PessoaCriar, PessoaEditar, PessoaLista } from "./recursos/orgaos";
import { AtestadoCriar, AtestadoEditar, AtestadoLista, DocumentosHabilitacao } from "./recursos/habilitacao";
import { TransportadoraCriar, TransportadoraEditar, TransportadoraLista } from "./recursos/transportadoras";
import { LicitacaoCriar, LicitacaoEditar } from "./recursos/licitacoes";
import { LicitacaoLista } from "./recursos/licitacao-lista";
import { BuscaPncp } from "./recursos/busca-pncp";
import { OneDriveRetorno } from "./recursos/onedrive";
import { Calendario } from "./recursos/calendario";

const recursos = (permissions: Permissoes | null) => {
  const admin = permissions?.papel === "admin";
  return (
    <>
      <Resource name="licitacao" list={LicitacaoLista} create={LicitacaoCriar} edit={LicitacaoEditar} recordRepresentation="numero" />
      <Resource name="v_licitacao" />
      <Resource name="pncp_contratacao" list={BuscaPncp} options={{ label: "Buscar licitações" }} />
      <Resource name="busca_filtro" />
      <Resource name="busca_ignorada" />
      <Resource name="pncp_coleta" />
      <Resource name="licitacao_item" />
      <Resource name="licitacao_arquivo" />
      <Resource name="licitacao_evento" />
      <Resource name="v_item_precificacao_atual" />
      <Resource name="modalidade" recordRepresentation="nome" />
      <Resource name="importacao_mapeamento" />
      <Resource name="pessoa" list={PessoaLista} create={PessoaCriar} edit={PessoaEditar} recordRepresentation="nome" />
      <Resource name="pessoa_contato" />
      <Resource name="unidade_compradora" />
      <Resource name="tipo_documento" recordRepresentation="nome" />
      <Resource name="documento_habilitacao" list={DocumentosHabilitacao} />
      <Resource name="v_documento_habilitacao" />
      <Resource name="atestado_capacidade" list={AtestadoLista} create={AtestadoCriar} edit={AtestadoEditar} />
      <Resource name="atestado_anexo" />
      <Resource
        name="transportadora"
        list={TransportadoraLista}
        create={TransportadoraCriar}
        edit={TransportadoraEditar}
        recordRepresentation="nome"
      />
      <Resource name="produto" list={ProdutoLista} create={ProdutoCriar} edit={ProdutoEditar} recordRepresentation="descricao" />
      <Resource name="fornecedor" list={FornecedorLista} create={FornecedorCriar} edit={FornecedorEditar} recordRepresentation="nome" />
      <Resource name="grupo_produto" create={GrupoCriar} edit={GrupoEditar} recordRepresentation="nome" />
      <Resource name="setor_fornecedor" create={SetorCriar} edit={SetorEditar} recordRepresentation="nome" />
      <Resource name="fabricante" create={FabricanteCriar} edit={FabricanteEditar} recordRepresentation="nome" />
      <Resource name="unidade_medida" create={UnidadeCriar} edit={UnidadeEditar} recordRepresentation="sigla" />
      <Resource name="produto_embalagem" />
      <Resource name="v_cotacao_preco" />
      <Resource name="v_custo_referencia" />
      <Resource name="cotacao_revalidacao" />
      <Resource name="produto_imagem_sugestao" />
      <Resource
        name="parametro_sistema"
        list={ParametroSistemaLista}
        {...(admin ? { create: ParametroSistemaCriar } : {})}
        options={{ label: "Parâmetros do sistema" }}
      />
      <Resource name="portal" list={PortalLista} create={PortalCriar} edit={PortalEditar} recordRepresentation="nome" />
      <Resource name="conta_portal" list={ContaPortalLista} create={ContaPortalCriar} edit={ContaPortalEditar} />
      {admin && <Resource name="empresa" list={DadosEmpresa} />}
      <Resource
        name="parametro_preco_padrao"
        list={ParametroPrecoLista}
        {...(admin ? { create: ParametroPrecoCriar } : {})}
        options={{ label: "Padrões de preço" }}
      />
      <Resource name="v_aliquota_icms_vigente" list={AliquotaIcmsLista} options={{ label: "Matriz de ICMS" }} />
      <Resource
        name="parametro_fiscal"
        list={ParametroFiscalLista}
        {...(admin ? { create: ParametroFiscalCriar } : {})}
        options={{ label: "Parâmetros fiscais" }}
      />
      <Resource
        name="regra_preco"
        list={RegraPrecoLista}
        {...(admin ? { create: RegraPrecoCriar } : {})}
        options={{ label: "Regra de preço" }}
      />
      <Resource name="papel" recordRepresentation="nome" />
      {admin && (
        <Resource name="usuario" list={UsuarioLista} edit={UsuarioEditar} recordRepresentation="nome">
          <Route path="convidar" element={<UsuarioConvidar />} />
        </Resource>
      )}
      {!admin && <Resource name="usuario" recordRepresentation="nome" />}
      {admin && <Resource name="auditoria" list={AuditoriaLista} />}
    </>
  );
};

export function App() {
  return (
    <BrowserRouter>
      <Admin
        title="Sobral Nunes — Licitações"
        dataProvider={dataProvider}
        authProvider={authProvider}
        i18nProvider={i18nProvider}
        loginPage={LoginPage}
        layout={Layout}
        dashboard={Painel}
        theme={tema}
        requireAuth
        disableTelemetry
      >
        <CustomRoutes noLayout>
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route path={ForgotPasswordPage.path} element={<ForgotPasswordPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="/onedrive/retorno" element={<OneDriveRetorno />} />
          <Route path="/calendario" element={<Calendario />} />
        </CustomRoutes>
        {recursos}
      </Admin>
    </BrowserRouter>
  );
}
